/**
 * Amending or voiding a sale after Create Final Sale.
 *
 * Winnie's Checks_20 Sep list, sections 7 to 9. The table (`sale_amendments`) and the three
 * columns on `sale_workspaces` were built on 13 September and, as the 14 September audit put
 * it, "Zero application code reads or writes it." This is that code.
 *
 * Four rules from her spec, each of which changes what the code does:
 *
 * 1. **The numbers are reused.** "no new deal number or invoice number is generated for a
 *    buyer or price amendment... the contract/tax invoice PDFs are regenerated under the same
 *    numbers, clearly marked as a revised version." So this re-issues through the existing
 *    document endpoint with `amended: true`, which already knows how to file a revision
 *    alongside the original rather than overwriting it.
 * 2. **A price change recalculates; a buyer change does not.** "Buyer-only changes (no price
 *    change) don't need a commission or margin-scheme recalculation — those figures don't
 *    depend on who the buyer is." They still need the reason, the snapshot and the AR flag.
 * 3. **A reason is mandatory.** Enforced here and by the table's own constraint.
 * 4. **The cooling-off window is a boundary, not a lock.** "A change needed after that window
 *    isn't covered by this spec — flag it for a decision... rather than silently allowing the
 *    same in-place edit indefinitely." So an out-of-window amendment is refused with a message
 *    that says what to do instead. It is not quietly permitted, and it is not made impossible
 *    either — `within_cooling_off` is recorded on every amendment so the ones that were
 *    inside the window can be told apart later.
 */
import { supabase } from '../lib/supabase'
import { User } from '../types'
import { apiHeadersOrThrow } from '../lib/authHeader'
import { recalculateAfterTermsChange, voidCommission } from './commissionReconService'
import { transitionDealStatus } from '../lib/dealStatusService'
import { ShippingParty } from './saleWorkspaceService'
import { ACTIONS, logAction } from './actionLog'

export type AmendmentType = 'BuyerChanged' | 'PriceChanged' | 'CancelledReassigned' | 'CancelledVoided'

export const AMENDMENT_LABEL: Record<AmendmentType, string> = {
  BuyerChanged: 'Buyer changed',
  PriceChanged: 'Price changed',
  CancelledReassigned: 'Cancelled — reassigned',
  CancelledVoided: 'Cancelled — voided',
}

export interface BuyerSnapshot {
  buyer_name?: string | null
  buyer_abn?: string | null
  buyer_address?: string | null
  buyer_lmct?: string | null
  buyer_contact_email?: string | null
  buyer_contact_mobile?: string | null
}

export interface SaleAmendment {
  id: string
  dealId: string
  amendmentType: AmendmentType
  reason: string
  previousBuyer: BuyerSnapshot | null
  newBuyer: BuyerSnapshot | null
  previousSalePrice: number | null
  newSalePrice: number | null
  withinCoolingOff: boolean | null
  amendedBy: string | null
  amendedAt: string
}

const toAmendment = (r: Record<string, unknown>): SaleAmendment => ({
  id: String(r.id),
  dealId: String(r.deal_id),
  amendmentType: r.amendment_type as AmendmentType,
  reason: String(r.reason || ''),
  previousBuyer: (r.previous_buyer as BuyerSnapshot) || null,
  newBuyer: (r.new_buyer as BuyerSnapshot) || null,
  previousSalePrice: r.previous_sale_price == null ? null : Number(r.previous_sale_price),
  newSalePrice: r.new_sale_price == null ? null : Number(r.new_sale_price),
  withinCoolingOff: r.within_cooling_off == null ? null : Boolean(r.within_cooling_off),
  amendedBy: (r.amended_by as string) || null,
  amendedAt: String(r.amended_at || ''),
})

export async function loadAmendments(dealId: string): Promise<SaleAmendment[]> {
  const { data, error } = await supabase
    .from('sale_amendments')
    .select('*')
    .eq('deal_id', dealId)
    .order('amended_at', { ascending: false })
  if (error) throw error
  return ((data || []) as unknown as Record<string, unknown>[]).map(toAmendment)
}

/** Is this sale still inside its seven days? */
export function withinCoolingOff(coolingOffExpiresAt?: string | null, now = new Date()): boolean {
  if (!coolingOffExpiresAt) return false
  return now.getTime() <= new Date(coolingOffExpiresAt).getTime()
}

export interface AmendResult {
  ok: boolean
  message: string
}

export interface AmendInput {
  dealId: string
  reason: string
  /** Omit to leave the buyer alone. */
  buyer?: BuyerSnapshot
  /** Omit to leave the price alone. */
  salePrice?: number
  /**
   * Who moves the car, when that changes as part of the amendment.
   *
   * Winnie, Checks_20 Sep §9 Path A: "If the new buyer arranges their own pickup instead of
   * continuing with Direct Auto freight, shipping_party also updates as part of the same
   * amendment, which switches Vehicle.status between sold_in_transit and sold_delivered
   * accordingly." Omit to leave it as it is.
   */
  shippingParty?: ShippingParty
  /** A reassignment after a cancelled sale, rather than an ordinary correction of terms. */
  reassigned?: boolean
  user: User
}

/**
 * Amend a finalised sale in place.
 *
 * The order matters: the snapshot is taken before anything moves, the workspace and the deal
 * are updated next, and only then are the documents re-issued and the downstream figures
 * restated. A failure part-way leaves the record of what was attempted rather than a silent
 * half-change.
 */
export async function amendSale(input: AmendInput): Promise<AmendResult> {
  const reason = String(input.reason || '').trim()
  if (!reason) {
    return { ok: false, message: 'Say why this sale is being amended. No amendment saves without a reason.' }
  }

  const priceChanged = input.salePrice != null
  const buyerChanged = !!input.buyer
  const shippingGiven = !!input.shippingParty
  if (!priceChanged && !buyerChanged && !shippingGiven) {
    return { ok: false, message: 'Nothing was changed. Set a new buyer, a new price, or who moves the car.' }
  }
  /* Winnie's enum has four amendment types and none of them is "shipping changed". She only
     ever moves the shipping party as part of a reassignment, so a shipping change on its own
     would have to be filed under a type that does not describe it. Refused rather than
     mislabelled. */
  if (shippingGiven && !input.reassigned && !priceChanged && !buyerChanged) {
    return {
      ok: false,
      message:
        'Who moves the car changes as part of a reassignment, not on its own. Use "Reassign to a new buyer" if the ' +
        'buyer has changed, or change the delivery arrangements on the freight or pickup workspace.',
    }
  }

  const [wsRes, dealRes] = await Promise.all([
    supabase.from('sale_workspaces').select('*').eq('deal_id', input.dealId).maybeSingle(),
    supabase.from('deals').select('id, deal_status, sale_price, invoice_id').eq('id', input.dealId).maybeSingle(),
  ])
  if (wsRes.error) throw wsRes.error
  if (dealRes.error) throw dealRes.error

  const ws = wsRes.data as Record<string, unknown> | null
  const deal = dealRes.data as Record<string, unknown> | null
  if (!ws || !deal) return { ok: false, message: 'That sale could not be loaded.' }
  if (!ws.finalised_at) {
    return { ok: false, message: 'This sale has not been finalised yet — change it on the workspace rather than amending it.' }
  }
  if (ws.is_voided) {
    return { ok: false, message: 'This sale was voided. A later sale of the same vehicle is a new sale, with its own deal number.' }
  }

  const inWindow = withinCoolingOff(ws.cooling_off_expires_at as string)
  if (!inWindow && !input.reassigned) {
    /* Winnie's boundary, stated rather than enforced silently. */
    return {
      ok: false,
      message:
        'The seven-day cooling-off window on this sale has closed, so it cannot be amended in place. ' +
        'A change after the window needs a decision on how to handle it — most likely a credit note and a new deal ' +
        'rather than editing a document past its cooling-off period. Raise it rather than working around it.',
    }
  }

  const previousBuyer: BuyerSnapshot = {
    buyer_name: (ws.buyer_name as string) || null,
    buyer_abn: (ws.buyer_abn as string) || null,
    buyer_address: (ws.buyer_address as string) || null,
    buyer_lmct: (ws.buyer_lmct as string) || null,
    buyer_contact_email: (ws.buyer_contact_email as string) || null,
    buyer_contact_mobile: (ws.buyer_contact_mobile as string) || null,
  }
  const previousPrice = deal.sale_price == null ? null : Number(deal.sale_price)

  const amendmentType: AmendmentType = input.reassigned
    ? 'CancelledReassigned'
    : priceChanged
      ? 'PriceChanged'
      : 'BuyerChanged'

  const { error: amErr } = await supabase.from('sale_amendments').insert([
    {
      deal_id: input.dealId,
      amendment_type: amendmentType,
      reason,
      previous_buyer: previousBuyer,
      new_buyer: input.buyer || null,
      previous_sale_price: previousPrice,
      new_sale_price: priceChanged ? input.salePrice : null,
      within_cooling_off: inWindow,
      amended_by: input.user?.id || null,
    },
  ])
  if (amErr) throw amErr

  if (buyerChanged) {
    const { error } = await supabase
      .from('sale_workspaces')
      .update({ ...input.buyer, is_amended: true, updated_at: new Date().toISOString() })
      .eq('deal_id', input.dealId)
    if (error) throw error
  } else {
    await supabase.from('sale_workspaces').update({ is_amended: true }).eq('deal_id', input.dealId)
  }

  if (priceChanged) {
    const { error } = await supabase.from('deals').update({ sale_price: input.salePrice }).eq('id', input.dealId)
    if (error) throw error
  }

  const notes: string[] = []

  /* Who moves the car, when that is part of the amendment (§9 Path A).
     The status only follows the shipping party when the vehicle is already in one of the two
     delivery states. Winnie is explicit that a reassignment is "not a status reset" - a deal
     still at deal_pending stays at deal_pending, and moving it here would invent a sale that
     has not been finalised. */
  if (input.shippingParty && input.shippingParty !== ws.shipping_party) {
    const { error } = await supabase
      .from('sale_workspaces')
      .update({ shipping_party: input.shippingParty, updated_at: new Date().toISOString() })
      .eq('deal_id', input.dealId)
    if (error) notes.push('who moves the car could not be changed')
    else {
      /* The workspace for whoever now moves it, opened the same way Create Final Sale opens
         it, and the one that is no longer needed cancelled rather than deleted. */
      if (input.shippingParty === 'DirectAutoShips') {
        await supabase
          .from('freight_out_workspaces')
          .upsert([{ deal_id: input.dealId, status: 'NotStarted', who: input.user?.id || null }], {
            onConflict: 'deal_id',
          })
      } else {
        await supabase.from('pickup_workspaces').upsert([{ deal_id: input.dealId }], { onConflict: 'deal_id' })
        await supabase
          .from('freight_out_workspaces')
          .update({ status: 'Cancelled', cancelled_reason: 'Buyer now arranges collection: ' + reason })
          .eq('deal_id', input.dealId)
      }

      const current = String(deal.deal_status || '')
      const target = input.shippingParty === 'DirectAutoShips' ? 'sold_in_transit' : 'sold_delivered'
      if ((current === 'sold_in_transit' || current === 'sold_delivered') && current !== target) {
        const moved = await transitionDealStatus(
          input.dealId,
          current as never,
          target as never,
          input.user?.id || undefined,
          input.user,
          'Sale amended — who moves the car changed: ' + reason,
        )
        if (!moved.success) notes.push('the vehicle status could not follow the shipping change (' + moved.message + ')')
      }
    }
  }

  /* The AR record follows the sale: the amount if the price moved, and the flag either way,
     because the chase team needs to know the buyer identity changed too. */
  const invoiceId = (deal.invoice_id as string) || null
  if (invoiceId) {
    const patch: Record<string, unknown> = {
      flagged_amended: true,
      flagged_amended_at: new Date().toISOString(),
      flagged_amended_reason: reason,
      flagged_amended_ack_by: null,
      flagged_amended_ack_at: null,
    }
    if (priceChanged) patch.total_amount = input.salePrice
    const { error } = await supabase.from('invoices').update(patch).eq('id', invoiceId)
    if (error) notes.push('the AR record could not be flagged')
  }

  /* Re-issued under the same deal and invoice numbers, marked as a revision. */
  try {
    const res = await fetch('/api/deals/sale-documents', {
      method: 'POST',
      headers: await apiHeadersOrThrow(),
      body: JSON.stringify({
        dealId: input.dealId,
        dealNumber: String(ws.deal_number || ''),
        amended: true,
        coolingOffExpiresAt: (ws.cooling_off_expires_at as string) || null,
      }),
    })
    const body = await res.json().catch(() => ({}))
    if (!res.ok || body?.success === false) {
      notes.push('the documents could not be re-issued (' + (body?.message || res.status) + ')')
    }
  } catch (e) {
    notes.push('the documents could not be re-issued (' + (e instanceof Error ? e.message : 'unknown') + ')')
  }

  /* Only a price change moves the money, so only a price change restates the figures. */
  let restated = ''
  if (priceChanged) {
    try {
      const r = await recalculateAfterTermsChange(
        input.dealId,
        'Sale amended — ' + AMENDMENT_LABEL[amendmentType] + ': ' + reason,
        input.user?.id,
      )
      restated = ' ' + r.message
    } catch (e) {
      notes.push('commission could not be restated (' + (e instanceof Error ? e.message : 'unknown') + ')')
    }
  }

  await logAction({
    dealId: input.dealId,
    action: ACTIONS.SALE_AMENDED,
    actorId: input.user?.id || null,
    details: { amendment_type: amendmentType, reason, within_cooling_off: inWindow },
  })

  return {
    ok: true,
    message:
      AMENDMENT_LABEL[amendmentType] +
      '. The deal and invoice numbers are unchanged and the documents have been re-issued as a revision.' +
      (input.shippingParty && input.shippingParty !== ws.shipping_party
        ? input.shippingParty === 'DirectAutoShips'
          ? ' Direct Auto now arranges delivery, and the vehicle is In Transit.'
          : ' The buyer now collects, and the vehicle is Delivered.'
        : '') +
      restated +
      (notes.length ? ' Note: ' + notes.join('; ') + '.' : ''),
  }
}

/**
 * Void a sale outright — Winnie's Path B, "no new buyer is found".
 *
 * Everything on her cascade, in an order chosen so that a failure leaves the vehicle
 * recoverable rather than half-sold: the record of why first, then the money, then the
 * status. A vehicle that ends up back in stock with its commission still active is a
 * reporting error somebody can find; a vehicle whose commission was voided but which is still
 * marked sold is not.
 */
export async function voidSale(dealId: string, reason: string, user: User): Promise<AmendResult> {
  const trimmed = String(reason || '').trim()
  if (!trimmed) return { ok: false, message: 'Say why this sale is being voided. No void saves without a reason.' }

  const [wsRes, dealRes] = await Promise.all([
    supabase.from('sale_workspaces').select('*').eq('deal_id', dealId).maybeSingle(),
    supabase.from('deals').select('id, deal_status, sale_price, invoice_id').eq('id', dealId).maybeSingle(),
  ])
  if (wsRes.error) throw wsRes.error
  if (dealRes.error) throw dealRes.error
  const ws = wsRes.data as Record<string, unknown> | null
  const deal = dealRes.data as Record<string, unknown> | null
  if (!ws || !deal) return { ok: false, message: 'That sale could not be loaded.' }
  if (ws.is_voided) return { ok: false, message: 'This sale is already voided.' }

  const notes: string[] = []

  await supabase.from('sale_amendments').insert([
    {
      deal_id: dealId,
      amendment_type: 'CancelledVoided',
      reason: trimmed,
      previous_buyer: {
        buyer_name: (ws.buyer_name as string) || null,
        buyer_abn: (ws.buyer_abn as string) || null,
        buyer_address: (ws.buyer_address as string) || null,
      },
      previous_sale_price: deal.sale_price == null ? null : Number(deal.sale_price),
      within_cooling_off: withinCoolingOff(ws.cooling_off_expires_at as string),
      amended_by: user?.id || null,
    },
  ])

  /* The documents are retained and marked void, never deleted. */
  const { error: wsErr } = await supabase
    .from('sale_workspaces')
    .update({ is_voided: true, documents_void: true, updated_at: new Date().toISOString() })
    .eq('deal_id', dealId)
  if (wsErr) throw wsErr

  try {
    const r = await voidCommission(dealId, 'Sale voided: ' + trimmed, user?.id)
    if (!r.ok) notes.push(r.message)
  } catch (e) {
    notes.push('commission could not be voided (' + (e instanceof Error ? e.message : 'unknown') + ')')
  }

  /* The margin scheme credit and G1 are derived from the sale price and the sold status on
     every read — there is no stored figure to zero. Clearing the sale price and moving the
     vehicle back into stock is what takes both to nil, which is why this is one write and not
     a second set of columns to keep in step. */
  const { error: dErr } = await supabase
    .from('deals')
    .update({ sale_price: null, sale_date: null, settled_at: null })
    .eq('id', dealId)
  if (dErr) notes.push('the sale figures could not be cleared')

  const invoiceId = (deal.invoice_id as string) || null
  if (invoiceId) {
    const { error } = await supabase
      .from('invoices')
      .update({
        cancelled_at: new Date().toISOString(),
        cancelled_reason: trimmed,
        status: 'cancelled',
      })
      .eq('id', invoiceId)
    if (error) notes.push('the AR record could not be cancelled')
  }

  await supabase
    .from('freight_out_workspaces')
    .update({ status: 'Cancelled', cancelled_reason: 'Sale voided: ' + trimmed })
    .eq('deal_id', dealId)

  const moved = await transitionDealStatus(
    dealId,
    String(deal.deal_status || '') as never,
    'paid' as never,
    user?.id || undefined,
    user,
    'Sale voided — back into available stock: ' + trimmed,
  )
  if (!moved.success) {
    notes.push('the vehicle status could not be moved back to Paid – Ready for Sale (' + moved.message + ')')
  }

  await logAction({
    dealId,
    action: ACTIONS.SALE_VOIDED,
    actorId: user?.id || null,
    details: { reason: trimmed },
  })

  return {
    ok: true,
    message:
      'Sale voided. The vehicle is back in available stock, the documents and the receivable are retained and marked ' +
      'void, and the commission is taken out of active totals. A later sale of this vehicle is a brand-new deal with ' +
      'its own number.' +
      (notes.length ? ' Note: ' + notes.join('; ') + '.' : ''),
  }
}

/**
 * Acknowledge the amended flag on a receivable.
 *
 * Winnie: "Clearing the flag is a manual acknowledgement action... not automatic on next page
 * load — an unacknowledged flag should persist until someone actually looks at it."
 */
export async function acknowledgeAmendedFlag(invoiceId: string, user: User): Promise<void> {
  const { error } = await supabase
    .from('invoices')
    .update({
      flagged_amended: false,
      flagged_amended_ack_by: user?.id || null,
      flagged_amended_ack_at: new Date().toISOString(),
    })
    .eq('id', invoiceId)
  if (error) throw error
}
